import { useParams } from 'react-router-dom'
import { Box, Divider, Paper, Stack, Typography } from '@mui/material'
import { skipToken } from '@reduxjs/toolkit/query'
import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/macro'

import colors from '@/assets/scss/_themes-vars.module.scss'
import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import PageContainer from '@/components/ui/containers/PageContainer'
import { useGetWaitingRoomQuery } from '@/core/services/waiting-room'
import { WaitingRoomForm } from '@/features/waiting-room'
import NotFoundPage from '@/pages/error/NotFoundPage'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { useEditWaitingRoomForm } from '../model/useEditWaitingRoomForm'

export function EditWaitingRoomPage() {
  useLingui()
  const { waitingRoomId } = useParams<{ waitingRoomId: string }>()

  const authUser = useAppSelector(selectAuthUser)

  const { data: waitingRoom, isLoading } = useGetWaitingRoomQuery(waitingRoomId ?? skipToken)

  if (isLoading) {
    return (
      <Stack flex={1} alignItems="center" justifyContent="center">
        <HeartbeatLoader sx={{ transform: 'scale(4)' }} />
      </Stack>
    )
  }

  if (!waitingRoom || waitingRoom.createdBy !== authUser?._id) return <NotFoundPage />

  return (
    <PageContainer>
      <Paper>
        <Stack direction="row" alignItems="center" justifyContent="space-between" p={3}>
          <Stack spacing={1} sx={{ flex: 1 }}>
            <Typography variant="h2" component="div" noWrap sx={{ fontSize: { sm: '20px', lg: '24px' }, lineHeight: '28px', fontWeight: 700 }}>
              {waitingRoom.name}
            </Typography>
            <Typography
              variant="subtitle1"
              component="div"
              noWrap
              sx={{ color: colors.gray500, fontSize: { sm: '16px', lg: '18px' }, lineHeight: '22px', fontWeight: 400 }}
            >
              <Trans>Edit waiting room</Trans>
            </Typography>
          </Stack>
        </Stack>
        <Divider />
        <Box p={3}>
          <EditWaitingRoomForm waitingRoomId={waitingRoom._id} />
        </Box>
      </Paper>
    </PageContainer>
  )
}

function EditWaitingRoomForm({ waitingRoomId }: { waitingRoomId: string }) {
  const { initialValues, onSubmit, isSubmitting } = useEditWaitingRoomForm(waitingRoomId)

  return <WaitingRoomForm waitingRoomId={waitingRoomId} initialValues={initialValues} isSubmitting={isSubmitting} onSubmit={onSubmit} />
}
